// ===== MypageHistoryContainer.js (完全版) =====
import React, { useEffect, useState } from 'react';
import { axiosInstance } from "../../utils/axios.js";
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Calendar, Clock, User, CreditCard, Truck, CheckCircle, ChevronRight, 
  Phone, Shield, FileText, AlertTriangle, UserCheck, MapPin, Package 
} from 'lucide-react';
import Footer from '../../components/Footer';
import './mypage.scss';

const MypageHistoryContainer = (props) => {
  const [completedReservations, setCompletedReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState({});

  useEffect(() => {
    axiosInstance.get('/api/medichat/reservations', {
      headers: {
        'uid': props.token,
        'Access-Control-Allow-Origin': '*'
      }
    })
    .then(response => {
      setCompletedReservations(response.data.completed_reservations || []);
      console.log('完了予約データ:', response.data.completed_reservations);
      setIsLoading(false);
    })
    .catch(error => {
      console.error("Error fetching reservations:", error);
      setIsLoading(false);
    });
  }, []);

  // 日時フォーマット用コンポーネント
  const DateTimeComponent = ({ isoDate }) => {
    const date = new Date(isoDate);
    
    const formattedDate = date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
    });
    
    const formattedTime = date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
    });
    
    return (
      <div className="datetime_component">
        <Calendar className="datetime_icon" />
        <span>{formattedDate}</span>
        <Clock className="datetime_icon" />
        <span>{formattedTime}〜</span>
      </div>
    );
  };

  // 発送日をフォーマットする関数
  const formatDeliveryDate = (isoDate) => {
    if (!isoDate) return '未定';
    
    const date = new Date(isoDate);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
    });
  };

  // ステップの展開/折りたたみ
  const toggleStep = (reservationId, stepIndex) => {
    setExpandedSteps(prev => ({
      ...prev,
      [`${reservationId}-${stepIndex}`]: !prev[`${reservationId}-${stepIndex}`]
    }));
  };

  // タスクステータスの取得
  const getStepStatus = (task) => {
    if (!task) return { text: '未開始', class: 'status-upcoming' };
    if (task.is_completed) return { text: '完了', class: 'status-complete' };
    
    if (task.is_patient_waiting) {
      return { text: '患者様のアクション待ち', class: 'status-warning' };
    }
    
    return { text: task.current_status || '進行中', class: 'status-in-progress' };
  };

  // タスクステップのレンダリング
  const renderTaskStep = (task, index, reservation, isCurrentStep = false) => {
    if (!task) return null;

    const taskTypes = {
      0: { label: '診察', icon: '🩺' },
      1: { label: 'お会計', icon: '💳' },
      2: { label: 'お薬受取', icon: '💊' },
      3: { label: '書類受取', icon: '📄' },
      4: { label: '保険証登録', icon: '🏥' }
    };

    const taskTypeValue = task.task_type_numeric || parseInt(task.task_type) || 0;
    const taskInfo = taskTypes[taskTypeValue] || { label: task.task_type_label || 'その他', icon: '❓' };
    const status = getStepStatus(task);
    const stepKey = `${reservation.id}-${index}`;
    const isExpanded = expandedSteps[stepKey] || isCurrentStep;

    return (
      <li key={task.id || index} className={`flow-step ${isCurrentStep ? 'current-step' : ''} ${task.is_completed ? 'completed' : 'upcoming'} ${isExpanded ? 'active' : ''}`}>
        <div className="step-header" onClick={() => !isCurrentStep && toggleStep(reservation.id, index)}>
          <div className="step-number">
            {task.is_completed ? <CheckCircle size={20} /> : <span>{index + 1}</span>}
          </div>
          <div className="step-title">
            <span className="step-icon">{taskInfo.icon}</span>
            {taskInfo.label}
            <span className={`step-status-badge ${status.class}`}>
              {status.text}
            </span>
          </div>
          <ChevronRight className="step-chevron" />
        </div>
        
        <div className="step-content">
          <div className="step-description">
            {renderTaskDetails(task, reservation)}
          </div>
        </div>
      </li>
    );
  };

  // タスク詳細のレンダリング
  const renderTaskDetails = (task, reservation) => {
    if (!task) return null;

    const taskType = task.task_type_numeric || parseInt(task.task_type) || task.task_type;

    switch(taskType) {
      case 0:
      case 'consultation':
        return (
          <div>
            <p>診察が完了しました。</p>
            {task.task_tags?.length > 0 && (
              <div className="task-tags">
                {task.task_tags.map(tag => (
                  <span key={tag.id} className="tag">{tag.tag_name}</span>
                ))}
              </div>
            )}
          </div>
        );
      
      case 4:
      case 'insurance_card':
        return (
          <div>
            {task.is_completed ? (
              <p>保険証の確認が完了しました。</p>
            ) : (
              <div className="warning-box">
                <AlertTriangle size={16} />
                <div>
                  <strong>保険証の登録が必要でした</strong>
                  {task.task_tags?.map(tag => (
                    <span key={tag.id} className="tag">{tag.tag_name}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 1:
      case 'accounting':
        return (
          <div>
            {task.is_completed ? (
              <p>お支払いが完了しました。</p>
            ) : (
              <p>会計処理中でした。</p>
            )}
          </div>
        );
      
      case 2:
      case 'medicine_delivery':
        return (
          <div>
            {task.is_completed ? (
              <div>
                <p>お薬の配送が完了しました。</p>
                {reservation.delivery_info && (
                  <div className="delivery-details">
                    <div className="delivery-date">
                      <Package size={16} />
                      <span>発送日: {formatDeliveryDate(reservation.delivery_info.deliver_time)}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p>お薬の配送処理中でした。</p>
            )}
          </div>
        );
      
      case 3:
      case 'document_creation':
        return (
          <div>
            {task.is_completed ? (
              <div>
                <p>書類の作成が完了しました。</p>
                {reservation.careplan_info?.has_careplan && (
                  <div className="careplan-download">
                    <a href={reservation.careplan_info.careplan_url} target="_blank" rel="noopener noreferrer" className="careplan-download-link">
                      <FileText size={16} />
                      療養計画書をダウンロード
                    </a>
                  </div>
                )}
                {reservation.careplan_info?.status === 'preparing' && (
                  <div className="careplan-preparing">
                    <FileText size={16} />
                    <span>療養計画書は準備中です</span>
                  </div>
                )}
              </div>
            ) : (
              <p>書類作成中でした。</p>
            )}
          </div>
        );
      
      default:
        return <p>{task.current_status || '処理中'}</p>;
    }
  };

  // 配送情報セクションのレンダリング
  const renderDeliveryInfoSection = (reservation) => {
    if (!reservation.delivery_info) return null;

    const { delivery_address, deliver_time } = reservation.delivery_info;
    
    return (
      <div className="delivery_info_section">
        <div className="delivery_info_header">
          <h4 className="delivery_info_title">
            <Truck size={20} />
            配送情報
          </h4>
        </div>
        
        <div className="delivery_info_content">
          <div className="delivery_date_info">
            <div className="delivery_date_label">
              <Package size={16} />
              発送日
            </div>
            <div className="delivery_date_value">
              {formatDeliveryDate(deliver_time)}
            </div>
          </div>
          
          <div className="delivery_address_info">
            <div className="delivery_address_label">
              <MapPin size={16} />
              発送先住所
            </div>
            <div className="delivery_address_content">
              <div className="delivery_address_details">
                <div>〒{delivery_address.zip}</div>
                <div>{delivery_address.address}</div>
                {delivery_address.apartment && <div>{delivery_address.apartment}</div>}
              </div>
              {delivery_address.phone && (
                <div className="delivery_phone">
                  <Phone size={14} />
                  {delivery_address.phone}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 現在のステップを判定
  const getCurrentStep = (reservation) => {
    if (!reservation.reservation_tasks || reservation.reservation_tasks.length === 0) {
      return 0;
    }

    const tasks = reservation.reservation_tasks;
    const sortedTasks = [...tasks].sort((a, b) => {
      const order = [0, 4, 1, 2, 3]; // 診察→保険証→会計→配送→書類
      const aType = a.task_type_numeric || parseInt(a.task_type) || 0;
      const bType = b.task_type_numeric || parseInt(b.task_type) || 0;
      return order.indexOf(aType) - order.indexOf(bType);
    });

    // 最後に未完了だったタスクを探す
    for (let i = sortedTasks.length - 1; i >= 0; i--) {
      if (!sortedTasks[i].is_completed) {
        return i;
      }
    }

    // 全て完了していたら最後のステップ
    return sortedTasks.length - 1;
  };

  if (isLoading) {
    return (
      <div>
        <div className="loader" style={{ width: '100px', height: '100px' }}></div>
        <div
          style={{
            width: '100%',
            position: 'absolute',
            bottom: '0%',
            height: '80px',
            background: '#FEFBF9',
            zIndex: '12',
          }}
        />
      </div>
    );
  }

  return (
    <div className="sunny_container">
      <div className="mypage_history container">
        <h1 className="mypage_history_h1">受診履歴</h1>

        {completedReservations.length === 0 ? (
          <div className="mypage_reservation_now_reservations" style={{width:'80%'}}>
            <Calendar className="empty_icon" />
            <p>受診履歴はまだありません。</p>
          </div>
        ) : (
          <div className="history-list">
            {completedReservations.map((reservation, index) => {
              const currentStepIndex = getCurrentStep(reservation);
              const isCancelled = reservation.cancel;
              
              return (
                <div key={reservation.id || index} className="history-card">
                  {/* ヘッダー情報 */}
                  <div className={`history-header ${isCancelled ? 'cancelled' : ''}`}>
                    <div className="history-info">
                      <div className="subject-info">
                        {reservation.first ? (
                          <span className="visit-badge first-visit">初診 #{reservation.subject?.name || '一般診療'}</span>
                        ) : (
                          <span className="visit-badge return-visit">再診 #{reservation.subject?.name || '一般診療'}</span>
                        )}
                        <User size={16} />
                        {reservation.admin_user?.lastname && (
                          <div className="doctor-info">
                            <span>担当医: {reservation.admin_user.lastname}先生</span>
                          </div>
                        )}
                      </div>
                    
                      <DateTimeComponent isoDate={reservation.start_time} />
                      
                      {isCancelled && (
                        <div className="cancelled-badge">
                          キャンセル済み
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 支払い・書類情報（お支払い完了後のみ表示、カルテ確認待ちは除外） */}
                  {!isCancelled && reservation.finish && reservation.payment && reservation.confirm_status && (
                    <div className="history-footer">
                      <div className="completion-info">
                        <div className="document-links">
                          {/* 領収書リンク */}
                          {reservation.bill_url && (
                            <a href={reservation.bill_url} target="_blank" rel="noopener noreferrer" className="document-link receipt-link">
                              <CreditCard size={16} />
                              領収書を見る
                            </a>
                          )}
                          
                          {/* 療養計画書リンク */}
                          {reservation.careplan_info?.has_careplan && (
                            <a href={reservation.careplan_info.careplan_url} target="_blank" rel="noopener noreferrer" className="document-link careplan-link">
                              <FileText size={16} />
                              療養計画書を見る
                            </a>
                          )}
                          
                          {/* 療養計画書準備中 */}
                          {reservation.careplan_info?.status === 'preparing' && (
                            <div className="document-preparing">
                              <FileText size={16} />
                              <span>療養計画書準備中</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* カルテ確認待ちの場合のメッセージ */}
                  {!isCancelled && reservation.finish && !reservation.confirm_status && (
                    <div className="history-footer" style={{textAlign: 'center'}}>
                      <div className="status_badge pending" style={{width: '100%', justifyContent: 'center'}}>
                        <Clock size={16} />
                        <span>カルテ確認中です。確定後に処方内容が表示されます。</span>
                      </div>
                    </div>
                  )}
                  
                  {/* お支払い待ちの場合のメッセージ */}
                  {!isCancelled && reservation.finish && reservation.confirm_status && !reservation.payment && (
                    <div className="history-footer" style={{textAlign: 'center'}}>
                      <div className="status_badge pending" style={{width: '100%', justifyContent: 'center'}}>
                        <CreditCard size={16} />
                        <span>お支払い完了後に書類が表示されます</span>
                      </div>
                    </div>
                  )}
                  
                  {/* 未払いの場合の支払いボタン */}
                  {reservation.confirm_status && !reservation.payment && (
                    <NavLink to={`/mypage/payment/bill/${reservation.id}`}>
                      <button className="payment-button">
                        <CreditCard size={16} />
                        お支払いへ
                      </button>
                    </NavLink>
                  )}

                  {/* お会計確定待ち */}
                  {!reservation.confirm_status && reservation.finish && (
                    <div className="status_badge pending" style={{textAlign:'center',width:'90%',justifyContent:'center'}}>
                      <Clock className="status_icon" />
                      <span>お会計確定待ち</span>
                    </div>
                  )}

                  {/* 発送待ち */}
                  {reservation.finish && reservation.confirm_status == true && reservation.payment == true && reservation.deliver == false && (
                    <div className="status_badge shipping_waiting" style={{textAlign:'center',width:'90%',justifyContent:'center'}}>
                      <Truck className="status_icon" />
                      <span>発送待ち</span>
                    </div>
                  )}

                  {/* 配送済み */}
                  {reservation.finish && reservation.confirm_status == true && reservation.payment == false && reservation.deliver == true && (
                    <div className="status_badge delivered" style={{textAlign:'center',width:'90%',justifyContent:'center'}}>
                      <CheckCircle className="status_icon" />
                      <span>配送済み</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MypageHistoryContainer;